import React, { useCallback } from "react";

import { useForm } from "react-hook-form-7";

import useTranslations from "../../hooks/useTranslations";

import {
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  HStack,
  Spacer,
  VStack
} from '@chakra-ui/react';

import { Input } from "../wombat-ui";
import useNotification from "../../hooks/useNotification";
import { useHistory } from "react-router";
import { useUserUpdatePassword } from '../../graphql/generated/definitions';

type UpdatePasswordInput = {
  password?: string;
  passwordConfirmation?: string;
};

type UpdatePasswordModalFormProps = {
  isOpen: boolean;
  onClose: () => void
};

const UpdatePasswordModalForm: React.FC<UpdatePasswordModalFormProps> = ({
  isOpen,
  onClose
}) => {
  const t = useTranslations();
  const history = useHistory();

  const { notifySuccess, notifyError } = useNotification();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    formState,
  } = useForm<UpdatePasswordInput>({ mode: "onChange" });

  const { password } = watch();

  const [userUpdatePassword] = useUserUpdatePassword();
  const onSubmit = useCallback(async ({ password, passwordConfirmation }) => {
    try {
      await userUpdatePassword({
        variables: {
          password,
          passwordConfirmation,
        },
      });
      notifySuccess({
        title: t(`userProfile.notification.success`) as string,
        description: t(`userProfile.notification.passwordUpdated`) as string,
      });
      history.push("/profile");
    } catch (error) {
      notifyError({
        title: t(`userProfile.notification.error`) as string,
      });
    }
  }, []);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
    >
      <ModalOverlay />
      <ModalContent>
        <form
          className="relative block"
          id="updatePasswordForm"
          onSubmit={handleSubmit(onSubmit)}
        >
          <ModalHeader>
            {t("userProfile.updatePassword")}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch">
              <Input
                id="password-first"
                label={t("userProfile.password") as string}
                error={errors?.password}
                type="password"
                autoComplete="new-password"
                {...register("password", {
                  minLength: {
                    value: 8,
                    message: t("forms.errors.min") as string,
                  },
                  required: t("forms.errors.required") as string,
                  validate: {
                    hasSpecialCharacter: (value) => 
                      /[$&+,:;=?@#|'<>.^*()%!-]/.test(value) || 
                      (t("userProfile.error.specialCharacter") as string)
                  }
                })}
              />
              <Spacer />
              <Input
                id="password-confirm"
                label={t("userProfile.passwordConfirmation") as string}
                error={errors?.passwordConfirmation}
                type="password"
                autoComplete="new-password"
                {...register("passwordConfirmation", {
                  required: true,
                  validate: {
                    equalToPassword: (value) =>
                      value === password ||
                      (t("userProfile.error.password") as string),
                  },
                })}
              />
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack>
              <Button
                onClick={onClose}
              >
                {t("forms.actions.cancel")}
              </Button>

              <Spacer />

              <Button
                type="submit"
                colorScheme="blue"
                disabled={!formState.isValid}
              >
                {t("userProfile.submit")}
              </Button>
            </HStack>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default React.memo(UpdatePasswordModalForm);
